import { useMemo, useState } from "react";
import { useImportProjects } from "@/hooks/use-monday-import";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";

interface Props {
  selectedCodes: string[];
  onChange: (codes: string[]) => void;
}

/**
 * Multi-select for project_codes — brukes som tilleggsfilter på Monday-import
 * når admin vil rydde opp ett prosjekt om gangen. Tom liste = importer alle
 * (ingen filtrering).
 */
export function ImportProjectSelector({ selectedCodes, onChange }: Props) {
  const { data: projects = [], isLoading } = useImportProjects();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return projects;
    return projects.filter((p) =>
      p.project_code.includes(s) ||
      (p.name ?? "").toLowerCase().includes(s),
    );
  }, [projects, search]);

  const toggle = (code: string) => {
    if (selectedCodes.includes(code)) {
      onChange(selectedCodes.filter((c) => c !== code));
    } else {
      onChange([...selectedCodes, code]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          className="text-xs"
        >
          <SrIcon name={open ? "chevron-down" : "chevron-right"} className="w-3 h-3 mr-1" />
          {selectedCodes.length === 0
            ? "Alle prosjekter"
            : `${selectedCodes.length} prosjekt${selectedCodes.length === 1 ? "" : "er"} valgt`}
        </Button>
        {selectedCodes.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange([])}
            className="text-xs text-muted-foreground"
          >
            Fjern filter
          </Button>
        )}
      </div>

      {selectedCodes.length > 0 && !open && (
        <div className="flex flex-wrap gap-1">
          {selectedCodes.map((c) => {
            const p = projects.find((x) => x.project_code === c);
            return (
              <button
                key={c}
                onClick={() => toggle(c)}
                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-muted hover:bg-muted/70"
              >
                {c}
                {p?.name && <span className="text-muted-foreground truncate max-w-[160px]">— {p.name}</span>}
                <SrIcon name="x" className="w-2.5 h-2.5" />
              </button>
            );
          })}
        </div>
      )}

      {open && (
        <div className="border-[1.5px] border-foreground/30 rounded p-2 space-y-2">
          <Input
            placeholder="Søk prosjektkode eller navn..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="max-h-72 overflow-y-auto space-y-0.5">
            {isLoading ? (
              <div className="text-xs text-muted-foreground p-2">Laster...</div>
            ) : filtered.length === 0 ? (
              <div className="text-xs text-muted-foreground p-2">Ingen treff</div>
            ) : (
              filtered.map((p) => {
                const sel = selectedCodes.includes(p.project_code);
                return (
                  <button
                    key={p.id}
                    onClick={() => toggle(p.project_code)}
                    className={cn(
                      "w-full flex items-center gap-2 px-2 py-1 rounded text-xs hover:bg-muted text-left",
                      sel && "bg-primary/10",
                    )}
                  >
                    <span
                      className={cn(
                        "w-4 h-4 rounded border-[1.5px] border-foreground/40 flex items-center justify-center shrink-0",
                        sel && "bg-primary border-primary",
                      )}
                    >
                      {sel && <SrIcon name="check" className="w-2.5 h-2.5 text-primary-foreground" />}
                    </span>
                    <span className="font-medium tabular-nums w-12">{p.project_code}</span>
                    <span className="truncate">{p.name ?? "(uten navn)"}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
